"use client"

import { createContext, useContext, useEffect, useState } from "react"

/**
 * Lets deep section content know which layout it is rendering into without
 * every section taking an `isNarrow` prop.
 *
 * Only used for genuinely layout-dependent decisions - chiefly suppressing a
 * detail view's own <h3> on mobile, where the drill-down header already shows
 * the same title right above it.
 */
export const MenuLayoutContext = createContext<{ isNarrow: boolean }>({ isNarrow: false })

export const useMenuLayout = () => useContext(MenuLayoutContext)

/** Below this width the menu switches to the single-column drill-down. */
const SINGLE_COLUMN_BREAKPOINT = 1024

/**
 * True when the menu should use the drill-down layout. `forceNarrow` lets the
 * caller honour the app's own isMobile detection even on a wide viewport (e.g. a
 * tablet reporting a large innerWidth).
 */
export const useIsNarrowViewport = (forceNarrow = false): boolean => {
  const [isNarrow, setIsNarrow] = useState(false)

  useEffect(() => {
    const check = () => setIsNarrow(window.innerWidth < SINGLE_COLUMN_BREAKPOINT)
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [])

  return forceNarrow || isNarrow
}
